// src/pages/TicketDesignTemplate/modals/UpdateTicketDesignTemplateModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import {
  deleteTicketDesignTemplateFile,
  fetchTicketDesignTemplateFileUrl,
  updateTicketDesignTemplate,
  updateTicketDesignTemplateFile,
  updateTicketDesignTemplateLayout,
} from 'slices/ticketDesignTemplate/thunk';
import { TicketDesignTemplate, DESIGN_UNIT_OPTIONS } from '../types';
import FileUploadPreview from '../components/FileUploadPreview';
import { toast } from 'react-toastify';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template');

interface UpdateTicketDesignTemplateModalProps {
  isOpen: boolean;
  template: TicketDesignTemplate;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdateTicketDesignTemplateModal: React.FC<UpdateTicketDesignTemplateModalProps> = ({
  isOpen,
  template,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { submitting } = useSelector((state: any) => state.TicketDesignTemplate);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileDeleted, setFileDeleted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const hasCurrentFile = Boolean(template.fileName && !fileDeleted);

  // Thu hồi blob URL của file mới chọn khi component unmount
  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation schema
  const validationSchema = Yup.object({
    templateName: Yup.string()
      .required(st('validation_template_name_required'))
      .max(200, st('validation_template_name_max')),
    designUnit: Yup.string()
      .oneOf(['Internal', 'External'], st('validation_design_unit_invalid'))
      .required(st('validation_design_unit_required')),
    issuancePeriod: Yup.string()
      .required(st('validation_issuance_period_required'))
      .matches(
        /^K\d+-\d{2}\/\d{4}$/,
        st('validation_issuance_period_format')
      ),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      templateName: template.templateName || '',
      designUnit: (template.designUnit || 'Internal') as 'Internal' | 'External',
      issuancePeriod: template.issuancePeriod || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const payload = {
          id: template.id!,
          ...values,
        };

        const result = await dispatch(updateTicketDesignTemplate(payload) as any);
        
        if (updateTicketDesignTemplate.fulfilled.match(result)) {
          if (selectedFile) {
            const fileResult = await dispatch(updateTicketDesignTemplateFile({
              id: template.id!,
              file: selectedFile,
            }) as any);

            if (!updateTicketDesignTemplateFile.fulfilled.match(fileResult)) {
              toast.error(fileResult.payload || st('khong_the_cap_nhat_file_mau_message'));
              return;
            }
          }

          toast.success(st('cap_nhat_mau_thiet_ke_thanh_message'));
          onSuccess();
          handleClose();
        } else {
          toast.error(
            typeof result.payload === 'string'
              ? result.payload
              : st('co_loi_xay_ra_khi_cap_message')
          );
        }
      } catch (error) {
        toast.error(st('co_loi_xay_ra_khi_cap_message'));
      }
    },
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(st('error_file_type'));
        return;
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > maxSize) {
        toast.error(st('error_file_size'));
        return;
      }

      setSelectedFile(file);

      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
      setPreviewImageUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPreviewImageUrl(null);
  };

  const handleOpenCurrentFile = async () => {
    if (!template.id) return;

    const result = await dispatch(fetchTicketDesignTemplateFileUrl(template.id) as any);
    if (fetchTicketDesignTemplateFileUrl.fulfilled.match(result) && result.payload) {
      window.open(String(result.payload), '_blank', 'noopener,noreferrer');
      return;
    }

    toast.error(result.payload || st('khong_the_tai_url_file_message'));
  };

  const handleDeleteCurrentFile = async () => {
    if (!template.id) return;

    const result = await dispatch(deleteTicketDesignTemplateFile(template.id) as any);
    if (deleteTicketDesignTemplateFile.fulfilled.match(result)) {
      setFileDeleted(true);
      // Xóa ảnh nền thì bố cục cũ không còn ý nghĩa — xóa luôn trên hệ thống
      if (template.layoutJson) {
        await dispatch(updateTicketDesignTemplateLayout({ id: template.id, layoutJson: null }) as any);
      }
      toast.success(st('da_xoa_file_mau_thiet_ke_message'));
      onSuccess();
      return;
    }

    toast.error(result.payload || st('khong_the_xoa_file_mau_thiet_message'));
  };

  // Handle close
  const handleClose = () => {
    formik.resetForm();
    handleRemoveFile();
    setFileDeleted(false);
    onClose();
  };

  // Format file size
  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      centered
    >
      <ModalHeader toggle={handleClose}>{st('chinh_sua_mau_thiet_ke_ve_title')}</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          {/* Hiển thị thông tin readonly */}
          <div className="alert alert-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{st('ma_mau_text_2')}</strong> {template.templateCode}
              </div>
              <div className="col-md-6">
                <strong>{st('trang_thai_text')}</strong>{' '}
                <span className="badge bg-warning">{template.statusDisplayName}</span>
              </div>
            </div>
          </div>

          <FormGroup>
            <Label for="templateName">
              {st('template_name_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="templateName"
              name="templateName"
              placeholder={st('template_name_placeholder')}
              maxLength={200}
              value={formik.values.templateName}
              data-action-code="edit-input-template-name"
              data-action-name="Tên bộ mẫu (Sửa)"
              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-template-name")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.templateName && !!formik.errors.templateName}
            />
            {formik.touched.templateName && formik.errors.templateName && (
              <FormFeedback>{formik.errors.templateName}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="designUnit">
              {st('design_unit_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              id="designUnit"
              name="designUnit"
              value={formik.values.designUnit}
              data-action-code="edit-input-design-unit"
              data-action-name="Đơn vị thiết kế (Sửa)"
              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-design-unit")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.designUnit && !!formik.errors.designUnit}
            >
              {DESIGN_UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            {formik.touched.designUnit && formik.errors.designUnit && (
              <FormFeedback>{formik.errors.designUnit}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="issuancePeriod">
              {st('issuance_period_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="issuancePeriod"
              name="issuancePeriod"
              placeholder={st('issuance_period_placeholder')}
              maxLength={20}
              value={formik.values.issuancePeriod}
              data-action-code="edit-input-issuance-period"
              data-action-name="Kỳ áp dụng (Sửa)"
              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-issuance-period")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.issuancePeriod && !!formik.errors.issuancePeriod}
            />
            {formik.touched.issuancePeriod && formik.errors.issuancePeriod && (
              <FormFeedback>{formik.errors.issuancePeriod}</FormFeedback>
            )}
            <small className="text-muted">{st('issuance_period_hint')}</small>
          </FormGroup>

          <FormGroup>
            <Label for="file">{st('design_file_label')}</Label>
            {hasCurrentFile && !selectedFile && (
              <div className="mb-2 p-2 bg-light rounded d-flex align-items-center justify-content-between gap-2">
                <i className="ri-file-line"></i> {st('file_hien_tai_text')} {template.fileName} ({
                  formatFileSize(template.fileSize)
                })
              </div>
            )}
            {hasCurrentFile && !selectedFile && (
              <div className="d-flex gap-2 mb-2">
                {PageActionPermission.canDisplay(location.pathname, "button-view-file") && (
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    type="button"
                    data-action-code="button-view-file"
                    data-action-name="Xem file mẫu vé hiện tại"
                    onClick={handleOpenCurrentFile}
                    disabled={submitting}
                  >
                    <i className="ri-eye-line me-1"></i>
                    {st('xem_file_button')}
                  </Button>
                )}
                {PageActionPermission.canDisplay(location.pathname, "button-delete-file") && (
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    type="button"
                    data-action-code="button-delete-file"
                    data-action-name="Xóa file mẫu vé hiện tại"
                    onClick={handleDeleteCurrentFile}
                    disabled={submitting}
                  >
                    <i className="ri-delete-bin-line me-1"></i>
                    {st('xoa_file_button')}
                  </Button>
                )}
              </div>
            )}
            <Input
              innerRef={fileInputRef}
              type="file"
              id="file"
              accept=".png,.jpg,.jpeg,.pdf"
              data-action-code="edit-input-file"
              data-action-name="File thiết kế vé (Sửa)"
              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-file")}
              onChange={handleFileChange}
            />
            <small className="text-muted">
              {st('design_file_hint')}
              {template.fileName && st('chon_file_moi_de_thay_the_text')}
            </small>
            {selectedFile && (
              <FileUploadPreview file={selectedFile} onRemove={handleRemoveFile} />
            )}
          </FormGroup>

          <div className="alert alert-warning">
            <i className="ri-alert-line"></i> {st('note_title')}
            <ul className="mb-0 mt-2">
              <li>{st('chi_co_the_chinh_sua_khi_text')}</li>
              <li>{st('sau_khi_phe_duyet_khong_the_text')}</li>
            </ul>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={submitting}>
            {st('huy_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-edit-submit") && (
            <Button
              color="primary"
              type="submit"
              data-action-code="button-edit-submit"
              data-action-name="Cập nhật mẫu vé"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {st('dang_cap_nhat_text')}
                </>
              ) : (
                st('cap_nhat_text')
              )}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UpdateTicketDesignTemplateModal;
